import {XMarkIcon} from '@heroicons/react/24/outline'
import React from 'react'
import {FormattedMessage} from 'react-intl'
import {Sponsor} from '../../hooks/useSponsors'
import Dialog from '../Dialog'
import FlatButton from '../FlatButton'
import Toast from '../Toast'

type Props = Readonly<{
  sponsor: Sponsor | null
  open: boolean
  error?: Error | null
  onClose: () => void
  onConfirm: (sponsor: Sponsor) => void
}>

function DeleteSponsorDialog({
  sponsor,
  open,
  error,
  onClose,
  onConfirm
}: Props) {
  if (!sponsor) {
    return null
  }

  const titleKey =
    sponsor.type === 'COMMUNITY'
      ? 'admin.manageSponsors.delete_community_title'
      : 'admin.manageSponsors.delete_national_title'

  const descriptionKey =
    sponsor.type === 'COMMUNITY'
      ? 'admin.manageSponsors.delete_community_description'
      : 'admin.manageSponsors.delete_national_description'

  return (
    <Dialog open={open} onClose={onClose}>
      <div className="flex flex-col items-center">
        <div className="-mt-6 mb-6 flex w-full items-center justify-between">
          <Dialog.Title className="typography-headline-4">
            <FormattedMessage id={titleKey} />
          </Dialog.Title>
          <button className="-mr-2 -mt-2" type="button" onClick={onClose}>
            <span className="sr-only">
              <FormattedMessage id="general.Close" />
            </span>
            <XMarkIcon aria-hidden className="size-7" />
          </button>
        </div>

        {error && (
          <div className="mb-4 w-full">
            <Toast variant="error">
              <FormattedMessage id="general.GenericError" />
            </Toast>
          </div>
        )}

        <div className="w-full space-y-6 text-center">
          <p className="typography-headline-6">
            <FormattedMessage
              id="admin.manageSponsors.delete_confirmation"
              values={{sponsorName: sponsor.name}}
            />
          </p>

          <p className="typography-body-2 text-grey-tertiary-30">
            <FormattedMessage id={descriptionKey} />
          </p>

          <p className="typography-body-2 font-semibold text-grey-tertiary-30">
            <FormattedMessage id="admin.manageSponsors.delete_warning" />
          </p>
        </div>

        <div className="mt-8 flex justify-center gap-4">
          <FlatButton variant="success" onClick={onClose} width="auto">
            <FormattedMessage id="admin.manageSponsors.cancel" />
          </FlatButton>
          <FlatButton
            variant="danger"
            onClick={() => onConfirm(sponsor)}
            width="auto"
          >
            <FormattedMessage id="admin.manageSponsors.confirm_deletion" />
          </FlatButton>
        </div>
      </div>
    </Dialog>
  )
}

export default DeleteSponsorDialog
